// NOTE: This file should normally not be modified unless you are adding a new provider.
// To add new routes, edit the AppRouter.tsx file.

import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { createHead, UnheadProvider } from '@unhead/react/client';
import { InferSeoMetaPlugin } from 'unhead/plugins';
import { Suspense } from 'react';
import NostrProvider from '@/components/NostrProvider';
import { NostrSync } from '@/components/NostrSync';
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { NostrLoginProvider } from '@nostrify/react/login';
import { AppProvider } from '@/components/AppProvider';
import { AppConfig } from '@/contexts/AppContext';
import { APP_RELAYS } from '@/lib/appRelays';
import AppRouter from './AppRouter';
const head = createHead({
  plugins: [
    InferSeoMetaPlugin(),
  ],
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 60000, // 1 minute
      gcTime: Infinity,
    },
  },
});

// Persist the query cache to localStorage so the map shows the last-known
// merchants instantly on every visit — no waiting for relays, no fluctuating
// count. Fresh data still loads in the background and replaces the cache.
const persister = createSyncStoragePersister({
  storage: window.localStorage,
  key: 'bch-local:query-cache',
});

// Only persist the merchant discovery queries (not auth, profiles, or
// admin-only data). This keeps localStorage small and avoids caching
// user-specific or sensitive results.
const PERSISTED_QUERY_PREFIXES: string[][] = [
  ['nostr', 'merchants'],
  ['nostr', 'bchnostr-verified'],
  ['nostr', 'verified-merchants'],
  ['nostr', 'sponsored-merchants'],
  ['nostr', 'approved-listings'],
];

function shouldPersistQuery(queryKey: readonly unknown[]): boolean {
  return PERSISTED_QUERY_PREFIXES.some((prefix) =>
    prefix.every((part, i) => queryKey[i] === part),
  );
}

const defaultConfig: AppConfig = {
  theme: "light",
  relayMetadata: APP_RELAYS,
  blossomServerMetadata: {
    servers: [
      'https://blossom.ditto.pub/',
      'https://blossom.dreamith.to/',
      'https://blossom.primal.net/',
    ],
    updatedAt: 0,
  },
  useAppBlossomServers: true,
};

export function App() {
  return (
    <UnheadProvider head={head}>
      <AppProvider storageKey="nostr:app-config" defaultConfig={defaultConfig}>
        <PersistQueryClientProvider
          client={queryClient}
          persistOptions={{
            persister,
            // Keep cached merchants for up to 7 days.
            maxAge: 7 * 24 * 60 * 60 * 1000,
            // Bump this string to invalidate all persisted data after schema changes.
            // v2: verified/sponsored/approved queries now return arrays instead
            // of Sets (Sets don't survive JSON serialization to localStorage).
            buster: 'v2',
            dehydrateOptions: {
              shouldDehydrateQuery: (query) => shouldPersistQuery(query.queryKey),
            },
          }}
        >
          <NostrLoginProvider storageKey='nostr:login'>
            <NostrProvider>
              <NostrSync />
              <TooltipProvider>
                <Toaster />
                <Suspense>
                  <AppRouter />
                </Suspense>
              </TooltipProvider>
            </NostrProvider>
          </NostrLoginProvider>
        </PersistQueryClientProvider>
      </AppProvider>
    </UnheadProvider>
  );
}

export default App;
